import type { ApiError } from '../api/errors';

export type RetryFailureClassification =
  /** The session is no longer valid; reuse the 6.1/6.2 convention of refetching the session
   * query, which `SessionProvider`/`ProtectedRoute` already react to. */
  | 'session_expired'
  /** `403 csrf_failed` or `500 retry_failed` - genuinely unexpected. Shown as a real error, and
   * the retry control stays available so the owner can try again. */
  | 'hard_error'
  /**
   * `404 note_not_found` or `409 note_not_retryable`. `docs/implementation/06-mobile-web.md`
   * 6.3 is explicit: "treat any `409` response as 'no longer retryable, refresh' rather than a
   * hard error banner" - the note changed status (or disappeared from this owner's view)
   * between the click and the server's answer, which is a stale screen, not a failure. `404` is
   * folded into the same case for the same reason: a race or a stale id should refresh the row
   * quietly rather than read as a crash.
   */
  | 'soft_refresh';

const SOFT_REFRESH_STATUSES: ReadonlySet<number> = new Set([404, 409]);

export function classifyRetryFailure(error: ApiError): RetryFailureClassification {
  if (error.isSessionInvalid) {
    return 'session_expired';
  }
  if (SOFT_REFRESH_STATUSES.has(error.status)) {
    return 'soft_refresh';
  }
  return 'hard_error';
}
